/**
 * Client directory accessors used by the client/contact pickers and the
 * /clients management surface.
 *
 * `queryClients` polls `GET /api/clients`, which returns the clients the caller may
 * raise jobs against. `createClient` mints a client via `POST /api/clients`. Contact
 * counterparts are scoped to a selected client id. Every function is a thin,
 * pluggable wrapper so the consuming components stay presentational.
 */

import {
  createClient as createClientApi,
  createClientContact as createContactApi,
  getClientContacts as getContactsApi,
  getClients,
  updateClientContact as updateContactApi,
} from "../../../lib/api";
import type { ClientContact, ClientSuggestion, CreateClientDraft, CreateContactDraft } from "../types";

/** Searches the client directory for the autocomplete query. */
export async function queryClients(query: string): Promise<ClientSuggestion[]> {
  const body = await getClients(query);
  return body.clients;
}

/** Creates a client and returns it as a suggestion. */
export async function createClient(draft: CreateClientDraft): Promise<ClientSuggestion> {
  const result = await createClientApi(draft);
  return {
    id: result.client.id,
    name: result.client.name,
    detail: result.client.email || result.client.phone || undefined,
  };
}

/** Lists contacts for a client (used to seed the contact combobox). */
export async function queryClientContacts(clientId: string): Promise<ClientContact[]> {
  const body = await getContactsApi(clientId);
  return body.contacts;
}

/** Creates a contact under a client and returns the full record. */
export async function createClientContact(
  clientId: string,
  draft: CreateContactDraft,
): Promise<ClientContact> {
  const result = await createContactApi(clientId, draft);
  return result.contact;
}

/** Updates a contact under a client and returns the full record. */
export async function updateClientContact(
  clientId: string,
  contactId: string,
  draft: CreateContactDraft,
): Promise<ClientContact> {
  const result = await updateContactApi(clientId, contactId, draft);
  return result.contact;
}
